import { IMPORT_FILE_EXTENSIONS, MAX_IMPORT_BYTES } from '@hearth/shared';

/**
 * Folder scan, browser side: walk a folder the user picked, keep the files the importer can read and spot identical
 * copies. Only names, sizes and dates are looked at here (plus the bytes of same-size files, hashed locally); nothing
 * leaves the computer until a file is checked or imported.
 */

/** Where the walk stops: a whole disk would take minutes and bury the few files that hold leads. */
export const SCAN_LIMITS = { entries: 20_000, candidates: 2_000 };
export type ScanLimits = typeof SCAN_LIMITS;

/** Program, cache and system folders (compared lower-case); dot and `$` folders are skipped too. */
const SKIPPED_FOLDERS: Record<string, true> = {
  node_modules: true,
  bower_components: true,
  __pycache__: true,
  __macosx: true,
  appdata: true,
  'application data': true,
  'local settings': true,
  'temporary internet files': true,
  'system volume information': true,
  windows: true,
  programdata: true,
  library: true,
  onedrivetemp: true,
};

export function isSkippedFolder(name: string): boolean {
  const lower = name.toLowerCase();
  // Hidden (.git, .Trash, .cache), system ($RECYCLE.BIN, $WinREAgent) and every "Program Files" variant.
  return lower.startsWith('.') || lower.startsWith('$') || lower.startsWith('program files') || SKIPPED_FOLDERS[lower] === true;
}

/** Office lock files (~$Book.xlsx) and hidden files (.~lock.Book.xlsx# from LibreOffice, ._Book.xlsx from a Mac). */
export function isSkippedFile(name: string): boolean {
  return name.startsWith('~$') || name.startsWith('.');
}

/** The file's extension when the importer reads that kind of file, else null. */
export function importExtension(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return null;
  const extension = name.slice(dot).toLowerCase();
  return (IMPORT_FILE_EXTENSIONS as readonly string[]).includes(extension) ? extension : null;
}

export type ScanCandidate = {
  /** Path inside the chosen folder, '/'-separated (the folder's own name left out). */
  path: string;
  name: string;
  extension: string;
  size: number;
  lastModified: number;
  /** Over MAX_IMPORT_BYTES: listed so it is not a mystery, but it cannot be checked or imported. */
  tooLarge: boolean;
  /** Path of an identical file earlier in the list (see findCopies). */
  copyOf: string | null;
  /** Opens the file; its bytes are read only when it is hashed, checked or imported. */
  file: () => Promise<File>;
};

export type ScanResult = {
  folderName: string;
  candidates: ScanCandidate[];
  /** Files and folders looked at (the inside of skipped folders not counted). */
  visited: number;
  skippedFolders: number;
  /** Why the walk ended before the end of the folder, if it did: a cap, or the user pressed Stop (the signal). */
  limit: keyof ScanLimits | 'stopped' | null;
};

export type ScanProgress = { visited: number; found: number };

export type WalkOptions = {
  limits?: ScanLimits;
  signal?: AbortSignal;
  onProgress?: (progress: ScanProgress) => void;
};

/** The parts of FileSystemDirectoryHandle / FileSystemFileHandle the walk uses (window.showDirectoryPicker). */
export type DirectoryLike = { kind: 'directory'; name: string; values(): AsyncIterable<DirectoryLike | FileLike> };
export type FileLike = { kind: 'file'; name: string; getFile(): Promise<File> };

const PROGRESS_EVERY = 250;

/** Counting and filtering shared by both ways of listing a folder. */
function collector(folderName: string, { limits = SCAN_LIMITS, signal, onProgress }: WalkOptions) {
  const result: ScanResult = { folderName, candidates: [], visited: 0, skippedFolders: 0, limit: null };
  const progress = () => onProgress?.({ visited: result.visited, found: result.candidates.length });
  return {
    result,
    progress,
    /** Counts one entry; false once the walk must end. */
    visit(): boolean {
      if (result.limit) return false;
      if (signal?.aborted) {
        result.limit = 'stopped';
        return false;
      }
      if (result.visited >= limits.entries) {
        result.limit = 'entries';
        return false;
      }
      result.visited++;
      if (result.visited % PROGRESS_EVERY === 0) progress();
      return true;
    },
    /** Name-only test, done before a file is opened. */
    wants: (name: string) => !isSkippedFile(name) && importExtension(name) !== null,
    add(path: string, name: string, size: number, lastModified: number, file: () => Promise<File>) {
      if (size === 0) return;
      // The cap trips on the first file past it, so a folder holding exactly the maximum is not reported as cut short.
      if (result.candidates.length >= limits.candidates) {
        result.limit = 'candidates';
        return;
      }
      result.candidates.push({ path, name, extension: importExtension(name)!, size, lastModified, tooLarge: size > MAX_IMPORT_BYTES, copyOf: null, file });
    },
  };
}

/**
 * Walks a folder picked with window.showDirectoryPicker, breadth first so the files near the top come before the
 * cap. Skipped folders are never opened, and a file is opened (for its size and date) only when its name qualifies.
 */
export async function walkDirectory(root: DirectoryLike, options: WalkOptions = {}): Promise<ScanResult> {
  const walk = collector(root.name, options);
  const queue: { dir: DirectoryLike; path: string }[] = [{ dir: root, path: '' }];
  for (let next = queue.shift(); next && !walk.result.limit; next = queue.shift()) {
    const { dir, path: dirPath } = next;
    try {
      for await (const entry of dir.values()) {
        if (!walk.visit()) break;
        const path = dirPath ? `${dirPath}/${entry.name}` : entry.name;
        if (entry.kind === 'directory') {
          if (isSkippedFolder(entry.name)) walk.result.skippedFolders++;
          else queue.push({ dir: entry, path });
          continue;
        }
        if (!walk.wants(entry.name)) continue;
        let file: File;
        try {
          file = await entry.getFile();
        } catch {
          continue; // Locked or gone since it was listed.
        }
        walk.add(path, entry.name, file.size, file.lastModified, () => entry.getFile());
      }
    } catch (error) {
      // A folder the browser may not open (permissions) is passed over; only the root failing is an error.
      if (dir === root) throw error;
    }
  }
  walk.progress();
  return walk.result;
}

/**
 * Lists the files of an `<input type="file" webkitdirectory>` (every browser). The browser has already listed the whole
 * folder, so skipped folders are dropped by path, and their contents do not count towards the entry cap.
 */
export function walkFileList(files: ArrayLike<File>, options: WalkOptions = {}): ScanResult {
  const first = files[0];
  const walk = collector(first ? (first.webkitRelativePath || first.name).split('/')[0]! : '', options);
  const skipped = new Set<string>();
  for (const file of Array.from(files)) {
    const segments = (file.webkitRelativePath || file.name).split('/');
    // The first segment is the chosen folder itself (it was picked on purpose), the last one the file name.
    const folders = segments.slice(1, -1);
    const blocked = folders.findIndex(isSkippedFolder);
    if (blocked !== -1) {
      skipped.add(folders.slice(0, blocked + 1).join('/'));
      continue;
    }
    if (!walk.visit()) break;
    if (!walk.wants(file.name)) continue;
    walk.add(segments.slice(1).join('/') || file.name, file.name, file.size, file.lastModified, () => Promise.resolve(file));
  }
  walk.result.skippedFolders = skipped.size;
  walk.progress();
  return walk.result;
}

/**
 * Marks identical files: only files of the same size can match, so only those are read and hashed. The original of a
 * set is the one nearest the top of the folder (backups sit deeper), then the first in list order; the others get
 * `copyOf` pointing at it. A file that cannot be read counts as unique. Stopping (the signal) keeps the copies found so far.
 */
export async function findCopies(
  candidates: readonly ScanCandidate[],
  hash: (candidate: ScanCandidate) => Promise<string>,
  signal?: AbortSignal,
): Promise<ScanCandidate[]> {
  const bySize = new Map<number, ScanCandidate[]>();
  for (const candidate of candidates) {
    if (candidate.tooLarge) continue;
    const group = bySize.get(candidate.size);
    if (group) group.push(candidate);
    else bySize.set(candidate.size, [candidate]);
  }
  const copyOf = new Map<string, string>();
  for (const group of bySize.values()) {
    if (group.length < 2) continue;
    const depth = (candidate: ScanCandidate) => candidate.path.split('/').length;
    const firstByHash = new Map<string, string>();
    for (const candidate of [...group].sort((a, b) => depth(a) - depth(b))) {
      if (signal?.aborted) break;
      let digest: string;
      try {
        digest = await hash(candidate);
      } catch {
        continue;
      }
      const first = firstByHash.get(digest);
      if (first) copyOf.set(candidate.path, first);
      else firstByHash.set(digest, candidate.path);
    }
  }
  return candidates.map((candidate) => {
    const first = copyOf.get(candidate.path);
    return first ? { ...candidate, copyOf: first } : candidate;
  });
}

/** SHA-256 of the file's bytes, read locally. crypto.subtle exists only in a secure context (https or localhost). */
export async function sha256(candidate: ScanCandidate): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await (await candidate.file()).arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
